import React from "react";
import { useState, useEffect, useRef } from "react";
import { StepContent } from "./wizard-components/StepContent";
import { MedicationService } from "@/services/MedicationService";
import { Pill, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  WizardMedication,
  MedicationForm,
  Medication,
} from "@/types/medication";
import { useMedicationStore } from "@/store/medicationStore";
import { v4 as uuidv4 } from "uuid";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface AddMedicationWizardProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  isInTab?: boolean;
  onComplete?: () => void;
}

export const AddMedicationWizard: React.FC<AddMedicationWizardProps> = ({
  open,
  onOpenChange,
  isInTab = false,
  onComplete,
}: AddMedicationWizardProps) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [addedMedication, setAddedMedication] = useState<WizardMedication | null>(null);
  const [countdownSeconds, setCountdownSeconds] = useState(3);
  // Reference to the confirmation box for accessibility focus management
  const confirmationRef = useRef<HTMLDivElement>(null);
  
  const [medication, setMedication] = useState<WizardMedication>({
    name: "",
    form: MedicationForm.Tablet, // Using proper enum value
    dosage: "",
    isPRN: false,
    cycle: "daily",
    foodInstructions: "any",
  });
  
  /**
   * Reset the medication form to its initial state
   * @description Resets all form fields to their default values
   */
  const resetForm = () => {
    setMedication({
      name: "",
      dosage: "",
      timing: "",
      form: MedicationForm.Tablet, // Using proper enum value
      isPRN: false,
      cycle: "daily",
      foodInstructions: "any",
    });
    setStep(1);
  };
  
  // Handle adding another medication manually
  const handleAddAnother = () => {
    setAddedMedication(null);
    setShowConfirmation(false);
    resetForm();
    // Call onComplete if provided
    if (onComplete) onComplete();
    // If we're not in a tab, close the dialog
    if (!isInTab && typeof onOpenChange === "function") {
      onOpenChange(false);
    }
  };

  // Auto-reset form after confirmation with countdown
  useEffect(() => {
    if (showConfirmation) {
      // Reset countdown when confirmation is shown
      setCountdownSeconds(3);

      // Set up interval to count down
      const countdownInterval = setInterval(() => {
        setCountdownSeconds(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Reset the form after 3 seconds
      const timer = setTimeout(() => {
        handleAddAnother();
      }, 3000);

      return () => {
        clearTimeout(timer);
        clearInterval(countdownInterval);
      };
    }
  }, [showConfirmation, isInTab, onOpenChange, onComplete]);

  const updateMedication = (newMedication: WizardMedication) => {
    setMedication(newMedication);
  };

  const totalSteps = 3;

  const handleNext = async (e?: React.MouseEvent) => {
    // Prevent event bubbling
    e?.preventDefault();
    e?.stopPropagation();

    if (step === 1) {
      if (!medication.name || !medication.dosage || !medication.form) {
        toast({
          title: "Required Fields",
          description: "Please fill in all required fields before continuing.",
          variant: "destructive",
        });
        return;
      }

      // Check for existing medications
      const { medications: currentMedications } = useMedicationStore.getState();
      const existingMeds = Object.values(currentMedications)
        .flat()
        .filter(
          (med) => med.name.toLowerCase() === medication.name.toLowerCase(),
        );

      if (existingMeds.length > 0) {
        toast({
          title: "Duplicate Medication",
          description:
            "This medication already exists in your list. Would you like to update the existing medication instead?",
          variant: "destructive",
        });
        return;
      }
    }

    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      try {
        await MedicationService.saveMedication(medication);

        const currentState = useMedicationStore.getState();

        // Force a re-render of components using current medications
        document.dispatchEvent(new CustomEvent("medicationUpdated", {
          detail: { medications: currentState.medications },
        }));
        
        // Store the added medication for confirmation display
        setAddedMedication({...medication});
        setShowConfirmation(true);

        toast({
          title: "Success",
          description: "Medication added successfully",
        });
      } catch (e) {
        // Removed complex console statement for security;
        toast({
          title: "Error",
          description: "Failed to add medication. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };



  // Handle keyboard interactions for accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showConfirmation && e.key === 'Escape') {
        setAddedMedication(null);
        setShowConfirmation(false);
        resetForm();
        if (onComplete) onComplete();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showConfirmation, onComplete]);

  // Focus management for accessibility
  useEffect(() => {
    if (showConfirmation && confirmationRef.current) {
      confirmationRef.current.focus();
    }
  }, [showConfirmation]);



  // Auto-reset form after confirmation with countdown
  useEffect(() => {
    if (showConfirmation) {
      // Reset countdown when confirmation is shown
      setCountdownSeconds(3);

      // Set up interval to count down
      const countdownInterval = setInterval(() => {
        setCountdownSeconds(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Reset the form after 3 seconds
      const timer = setTimeout(() => {
        handleAddAnother();
      }, 3000);

      return () => {
        clearTimeout(timer);
        clearInterval(countdownInterval);
      };
    }
  }, [showConfirmation, onComplete, handleAddAnother]);

  // Handle keyboard interactions for accessibility
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showConfirmation && e.key === 'Escape') {
        handleAddAnother();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showConfirmation]);

  // Focus management for accessibility
  useEffect(() => {
    if (showConfirmation && confirmationRef.current) {
      confirmationRef.current.focus();
    }
  }, [showConfirmation]);

  return (
    <div className="space-y-4">
      <AnimatePresence mode="wait">
        {showConfirmation && addedMedication ? (
          <motion.div 
            key="confirmation"
            ref={confirmationRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ 
              type: "spring", 
              stiffness: 500, 
              damping: 30 
            }}
            className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6 space-y-4"
            role="alert"
            aria-labelledby="confirmation-title"
            tabIndex={-1}
          >
            <motion.div 
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <motion.div 
                  initial={{ rotate: -20 }}
                  animate={{ rotate: 0 }}
                  transition={{ type: "spring", stiffness: 150 }}
                  className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center"
                >
                  <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-300" />
                </motion.div>
                <div>
                  <h3 id="confirmation-title" className="text-lg font-semibold text-green-800 dark:text-green-300">Medication Added!</h3>
                  <p className="text-sm text-green-600 dark:text-green-400">Your medication has been added to your schedule</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-100 dark:border-green-800/50 shadow-sm"
              tabIndex={0}
            >
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Name</p>
                  <p className="font-medium dark:text-white">{addedMedication.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Dosage</p>
                  <p className="font-medium dark:text-white">{addedMedication.dosage}</p>
                </div>
                {addedMedication.timing && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Timing</p>
                    <p className="font-medium dark:text-white">{addedMedication.timing}</p>
                  </div>
                )}
                {addedMedication.form && (
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Form</p>
                    <p className="font-medium dark:text-white">{addedMedication.form}</p>
                  </div>
                )}
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center pt-2"
            >
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Redirecting in <span className="font-semibold">{countdownSeconds}</span> seconds...
              </p>
              <motion.div 
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 3, ease: "linear" }}
                className="mt-2 h-1 bg-green-200 dark:bg-green-700 rounded-full"
                aria-hidden="true"
              />
              <Button
                onClick={handleAddAnother}
                className="mt-4 bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-700 dark:hover:bg-purple-600 transition-colors"
                size="sm"
              >
                Add another medication
              </Button>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div 
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4">

            <div className="mb-6">
              <div className="flex flex-row">
                <div className="w-1/3 text-left pr-4">
                  <h4 className="font-medium">
                    Step {step} of {totalSteps}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {step === 1 && "Enter medication details"}
                    {step === 2 && "Set schedule and timing"}
                    {step === 3 && "Add food instructions"}
                  </p>
                </div>
                <div className="w-2/3 flex items-center justify-center">
                  {Array.from({ length: totalSteps }).map((_, i) => (
                    <div key={i} className="flex items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                          i + 1 === step
                            ? "bg-[#8B5CF6] text-white"
                            : i + 1 < step
                              ? "bg-purple-200"
                              : "bg-gray-200"
                        }`}
                      >
                        {i + 1 < step ? "✓" : i + 1}
                      </div>
                      {i < totalSteps - 1 && (
                        <div
                          className={`w-12 h-1 mx-1 transition-all duration-300 ${
                            i + 1 < step ? "bg-purple-200" : "bg-gray-200"
                          }`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <StepContent
                step={step}
                currentMedication={medication}
                selectedMedications={[]}
                updateCurrentMedication={updateMedication}
              />

              <div className="flex justify-between pt-6">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={step === 1}
                  className="min-w-[100px] rounded-xl transition-all duration-300 hover:bg-purple-50 disabled:opacity-50"
                >
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  className="min-w-[100px] bg-purple-600 hover:bg-purple-700"
                >
                  {step === totalSteps ? "Finish" : "Continue"}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};